import apisauce, { type ApiResponse } from 'apisauce'
import Moment from 'moment'
import { camelizeKeys } from 'humps'
import I18n from 'APP/I18n'
import Config from '../../Config'
import type {
  Patient,
  ItemPatient,
  GenderIdentity,
  EnrichedEncounter,
  PageEnrichedEncounter,
  ItemUnionPractitionerBasicPractitioner,
  ItemEnrichedEpisode,
  PageQuestionnaire,
  ItemS3FileDownloadParameters,
  ItemS3FileUploadParameters,
  S3FileUploadParameters,
  S3BucketFields,
  MembersApiGetQuestionnairesRequest,
} from '@dialogue/coredata'
import type {
  MappedPatient,
  MappedEnrichedEncounter,
  MappedEnrichedEpisode,
  PatientEncountersResponse,
  PatientResponse,
  ConsentStatus,
} from './index.types'

export const mapPatientFromCoreDataV1 = (patient: Patient): MappedPatient => {
  // Because CoreData v1 returns *some fields* of the model with a different casing,
  // we need to map them here to avoid modifying the whole code base
  const {
    given_name,
    family_name,
    date_of_birth,
    id_card,
    phone_number,
    admin_area_iso_code,
    has_consented,
    user_type,
    ...formattedPatient
  } = patient
  return {
    ...formattedPatient,
    givenName: patient['given_name'],
    preferredName: patient['preferred_name'],
    pronouns: patient['pronouns'],
    familyName: patient['family_name'],
    dateOfBirth: patient['date_of_birth'],
    idCard: patient['id_card'],
    phoneNumber: patient['phone_number'],
    adminAreaISOCode: patient['admin_area_iso_code'],
    genderIdentity: patient['gender_identity'],
    genderIdentityDescription: patient['gender_identity_description'],
    hasConsented: patient['has_consented'],
    userType: patient['user_type'],
    profileSetupStatus: patient['profile_setup_status'],
  }
}

export const mapEncoutersFromCoreDataV1 = (
  encounters: EnrichedEncounter[]
): MappedEnrichedEncounter[] => {
  return encounters.map((encounter) => {
    const {
      careplans,
      diagnostic_orders,
      end_time,
      is_open,
      medication_orders,
      patient_id,
      practitioner_id,
      rtc_session,
      start_time,
      ...formattedEncounter
    } = encounter
    return {
      ...formattedEncounter,
      carePlans: encounter['careplans'],
      diagnosticOrders: encounter['diagnostic_orders'],
      endTime: encounter['end_time'],
      isOpen: encounter['is_open'],
      medicationOrders: encounter['medication_orders'],
      patient: mapPatientFromCoreDataV1(encounter['patient']),
      patientId: encounter['patient_id'],
      practitionerId: encounter['practitioner_id'],
      rtcSession: encounter['rtc_session'],
      startTime: start_time,
    }
  })
}

export const mapPatientToCoreDataV1 = (patient: MappedPatient): Patient => {
  //Because CoreData v1 needs *some fields* of the model with a different casing,
  //we need to map them here to avoid modifying the whole code base
  const {
    preferredName,
    pronouns,
    genderIdentity,
    genderIdentityDescription,
    adminAreaISOCode,
    ...formattedPatient
  } = patient

  const result: Patient = {
    ...formattedPatient,
    admin_area_iso_code: adminAreaISOCode,
    resides_in: adminAreaISOCode ? adminAreaISOCode.split('-')[1] : undefined,
    preferred_name: preferredName,
    pronouns: pronouns,
    gender_identity: genderIdentity as GenderIdentity | undefined,
    gender_identity_description: genderIdentityDescription,
    profile_setup_status: 'complete',
  }
  return result
}

// our "constructor"
const create = (accessToken: string, baseURL = `${Config.EMERGENCY_ROOM_DOMAIN}/v1`) => {
  const api = apisauce.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + accessToken,
    },
    // 30 second timeout...
    timeout: 30000,
  })

  const hasToken = (): boolean => {
    return !!api.headers['Authorization']
  }

  const resetToken = (): void => {
    api.deleteHeader('Authorization')
  }

  const getPatient = (): Promise<PatientResponse> => {
    // Because CoreData v1 returns *some fields* of the model with a different casing,
    // we need to map them here to avoid modifying the whole code base
    return api.get<ItemPatient>('/patients/me').then((response: ApiResponse<ItemPatient>) => {
      const itemPatient = response.data
      return {
        ...response,
        data: {
          ...itemPatient,
          data:
            response.ok && itemPatient?.data ? mapPatientFromCoreDataV1(itemPatient.data) : null,
        },
      }
    })
  }

  const getPatientEncounters = (): Promise<PatientEncountersResponse> => {
    return api
      .get<PageEnrichedEncounter>(`/patients/me/encounters?limit=100`)
      .then((response: ApiResponse<PageEnrichedEncounter>) => {
        // Because CoreData v1 returns *some fields* of the model with a different casing,
        // we need to map them here to avoid modifying the whole code base
        const pageEnrichedEncounter = response.data
        return {
          ...response,
          data: {
            ...pageEnrichedEncounter,
            data:
              response.ok && pageEnrichedEncounter?.data
                ? mapEncoutersFromCoreDataV1(pageEnrichedEncounter.data)
                : null,
          },
        }
      })
  }

  const getPatientMedicalIdURL = (): Promise<ApiResponse<ItemS3FileDownloadParameters>> => {
    return api
      .get<ItemS3FileDownloadParameters>('/patients/me/medical_id/download')
      .then((response) => response)
  }

  const getPatientMedicalIdUploadURL = (): Promise<ApiResponse<ItemS3FileUploadParameters>> => {
    return api
      .post<ItemS3FileUploadParameters>('/patients/me/medical_id/upload')
      .then((response) => response)
  }

  type FileObj = {
    fileUri?: string
    file?: File
  }

  // Note: This is unfortunately not the same code path as updating a child's id.
  // We should probably consolidate this code replication
  const uploadPatientMedicalID = (
    request: S3FileUploadParameters,
    fileObj: FileObj
  ): Promise<ApiResponse<S3FileUploadParameters>> => {
    let path = fileObj?.fileUri || ''
    if (
      fileObj?.fileUri?.indexOf('content://') === -1 &&
      fileObj?.fileUri?.indexOf('file://') === -1
    ) {
      path = `file://${path}`
    }

    const photo = fileObj?.file
      ? fileObj.file
      : {
          uri: path,
          type: 'image/jpeg',
          name: 'photo.jpg',
        }

    const fields = request.fields || {}
    let body = new FormData()

    Object.keys(fields).forEach((key) => {
      body.append(key, fields[key as keyof S3BucketFields])
    })
    body.append('file', photo as Blob)

    const aws = apisauce.create({
      baseURL: request.url,
      headers: {
        'Cache-Control': 'no-cache',
        'Content-Type': 'multipart/form-data',
      },
      timeout: 20000,
    })

    return aws.post<S3FileUploadParameters>(request.url, body).then((response) => {
      return response
    })
  }

  const updatePatient = (payload: MappedPatient): Promise<ApiResponse<ItemPatient>> => {
    if (payload.dateOfBirth) {
      payload.dateOfBirth = Moment(payload.dateOfBirth).format(I18n.t('DateFormat'))
    }
    return api
      .patch<ItemPatient>('/patients/me', mapPatientToCoreDataV1(payload))
      .then((response) => {
        return response
      })
  }

  const getMemberQuestionnaires = ({
    memberId,
    params,
  }: {
    memberId: string | number
    params?: MembersApiGetQuestionnairesRequest
  }): Promise<ApiResponse<PageQuestionnaire>> => {
    return api
      .get<PageQuestionnaire>(`/members/${memberId}/properties/questionnaires`, params)
      .then((response: ApiResponse<PageQuestionnaire>) => response)
  }

  const getEpisode = (episodeId: string): Promise<MappedEnrichedEpisode | null> => {
    return api
      .get<ItemEnrichedEpisode>(`/patients/me/episodes/${episodeId}`)
      .then((response: ApiResponse<ItemEnrichedEpisode>) => {
        return response.data?.data
          ? (camelizeKeys(response.data.data) as MappedEnrichedEpisode)
          : null
      })
  }

  const updateEpisodeState = (
    episodeId: string,
    state: string
  ): Promise<ApiResponse<ItemEnrichedEpisode>> => {
    return api
      .post<ItemEnrichedEpisode>(`/episodes/${episodeId}/state`, { state_id: state })
      .then((response: ApiResponse<ItemEnrichedEpisode>) => {
        if (response.ok) {
          return response
        } else {
          throw {
            status: response.status,
            title: `Unable to update episode state: ${state}`,
            detail: (response.data as { detail?: string })?.detail,
          }
        }
      })
  }

  const getPractitioner = (
    practitionerId: string
  ): Promise<ApiResponse<ItemUnionPractitionerBasicPractitioner>> =>
    api
      .get<ItemUnionPractitionerBasicPractitioner>(`/practitioners/${practitionerId}`)
      .then((response: ApiResponse<ItemUnionPractitionerBasicPractitioner>) => response)

  const getConsentStatus = ({
    patientId,
    consentBrand,
    patientConsentVersion,
    guardianConsentVersion,
  }: {
    patientId: string
    consentBrand: string
    patientConsentVersion: string
    guardianConsentVersion: string
  }) => {
    const params = {
      consent_brand: consentBrand,
      patient_consent_version: patientConsentVersion,
      ...(guardianConsentVersion && { guardian_consent_version: guardianConsentVersion }),
    }

    return api
      .get<ConsentStatus>(`/members/${patientId}/properties/consents`, params)
      .then((response: ApiResponse<ConsentStatus>) => {
        if (response.ok) {
          return response
        } else {
          throw new Error('Unable to get consent status')
        }
      })
  }
  return {
    hasToken,
    resetToken,
    getPatient,
    getPatientEncounters,
    getPatientMedicalIdURL,
    getPatientMedicalIdUploadURL,
    uploadPatientMedicalID,
    updatePatient,
    getMemberQuestionnaires,
    getEpisode,
    updateEpisodeState,
    getPractitioner,
    getConsentStatus,
  }
}

export default {
  create,
}
